export interface SessionProfile {
  user: { id: string; email: string; fullName: string };
  tenantId: string | null;
  permissions: string[];
  organizations: { id: string; name: string }[];
}
export interface Page<T> {
  success: true;
  data: T[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}
export interface TokenStore {
  get(): Promise<string | null>;
  set(token: string): Promise<void>;
  clear(): Promise<void>;
}
export class ApiClientError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string,
  ) {
    super(message);
  }
}
export class ObsoleteRequest extends Error {
  constructor() {
    super("Solicitud de un contexto anterior descartada");
  }
}
interface TokenResult {
  accessToken: string;
  refreshToken?: string;
}

/** Shared transport. Only native clients receive a secure persistent token store. */
export class ApiClient {
  private access: string | null = null;
  private epoch = 0;
  private refreshing: Promise<void> | null = null;
  private changing = false;
  onExpired: () => void = () => undefined;
  constructor(
    private readonly baseUrl: string,
    private readonly platform: "web" | "mobile",
    private readonly store?: TokenStore,
  ) {}

  private async send(path: string, method: string, body: unknown, authenticated: boolean) {
    let response: Awaited<ReturnType<typeof fetch>>;
    try {
      response = await fetch(`${this.baseUrl.replace(/\/$/, "")}${path}`, {
        method,
        credentials: this.platform === "web" ? "include" : "omit",
        headers: {
          "Content-Type": "application/json",
          "X-ERP-Client": this.platform,
          ...(authenticated && this.access ? { Authorization: `Bearer ${this.access}` } : {}),
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    } catch {
      throw new ApiClientError(
        "No se pudo conectar con la API. Revisa la conexión y la URL configurada.",
        0,
        "NETWORK_ERROR",
      );
    }
    let result: {
      success: boolean;
      data?: unknown;
      error?: { message?: string; code?: string };
      pagination?: Page<unknown>["pagination"];
    };
    try {
      result = await response.json();
    } catch {
      throw new ApiClientError(
        "La API devolvió una respuesta inválida",
        response.status,
        "INVALID_RESPONSE",
      );
    }
    if (!response.ok || !result.success)
      throw new ApiClientError(
        result.error?.message ?? "No se pudo completar la operación",
        response.status,
        result.error?.code ?? "API_ERROR",
      );
    return result;
  }
  private async accept(tokens: TokenResult, epoch: number) {
    if (epoch !== this.epoch) throw new ObsoleteRequest();
    if (this.platform === "mobile" && tokens.refreshToken) {
      if (!this.store)
        throw new ApiClientError(
          "Almacenamiento seguro no disponible",
          0,
          "SECURE_STORAGE_REQUIRED",
        );
      await this.store.set(tokens.refreshToken);
      if (epoch !== this.epoch) {
        await this.store.clear();
        throw new ObsoleteRequest();
      }
    }
    this.access = tokens.accessToken;
  }
  private async refresh(): Promise<void> {
    if (this.refreshing) return this.refreshing;
    const epoch = this.epoch;
    this.refreshing = (async () => {
      const refreshToken = this.platform === "mobile" ? await this.store?.get() : undefined;
      if (this.platform === "mobile" && !refreshToken)
        throw new ApiClientError("Inicia sesión", 401, "UNAUTHORIZED");
      const result = await this.send(
        "/auth/refresh",
        "POST",
        refreshToken ? { refreshToken } : {},
        false,
      );
      await this.accept(result.data as TokenResult, epoch);
    })().finally(() => {
      this.refreshing = null;
    });
    return this.refreshing;
  }
  async request<T>(path: string, method = "GET", body?: unknown): Promise<T> {
    if (this.changing) throw new ObsoleteRequest();
    const epoch = this.epoch;
    try {
      let result;
      const attemptedAccess = this.access;
      try {
        result = await this.send(path, method, body, true);
      } catch (error) {
        if (!(error instanceof ApiClientError) || error.status !== 401 || epoch !== this.epoch)
          throw error;
        // A late 401 may belong to the token another request has already renewed.
        if (this.access === attemptedAccess || !this.access) await this.refresh();
        if (epoch !== this.epoch) throw new ObsoleteRequest();
        result = await this.send(path, method, body, true); // exactly one refresh/retry
      }
      if (epoch !== this.epoch) throw new ObsoleteRequest();
      return (result.pagination ? result : result.data) as T;
    } catch (error) {
      if (epoch !== this.epoch) throw new ObsoleteRequest();
      if (error instanceof ApiClientError && error.status === 401) {
        this.access = null;
        this.epoch += 1;
        await this.store?.clear();
        this.onExpired();
      }
      throw error;
    }
  }
  async login(email: string, password: string): Promise<SessionProfile> {
    this.epoch += 1;
    const epoch = this.epoch;
    const result = await this.send(
      "/auth/login",
      "POST",
      { email, password, client: this.platform },
      false,
    );
    await this.accept(result.data as TokenResult, epoch);
    return this.request<SessionProfile>("/auth/me");
  }
  async forgotPassword(email: string): Promise<string> {
    const result = await this.send("/auth/forgot-password", "POST", { email }, false);
    return (
      (result as { message?: string }).message ??
      "Si existe una cuenta asociada, recibirás instrucciones para restablecer tu contraseña."
    );
  }
  async resetPassword(token: string, newPassword: string): Promise<string> {
    const result = await this.send("/auth/reset-password", "POST", { token, newPassword }, false);
    return (
      (result as { message?: string }).message ??
      "Contraseña actualizada. Inicia sesión nuevamente."
    );
  }
  async restore(): Promise<SessionProfile> {
    await this.refresh();
    return this.request<SessionProfile>("/auth/me");
  }
  async switchOrganization(id: string): Promise<SessionProfile> {
    if (this.changing) throw new ObsoleteRequest();
    if (this.refreshing) await this.refreshing;
    // Ensure valid access before invalidating old in-flight reads.
    await this.request<SessionProfile>("/auth/me");
    this.changing = true;
    this.epoch += 1;
    try {
      const result = await this.send(
        "/auth/switch-organization",
        "POST",
        { organizationId: id },
        true,
      );
      this.access = (result.data as TokenResult).accessToken;
    } finally {
      this.changing = false;
    }
    return this.request<SessionProfile>("/auth/me");
  }
  async logout(): Promise<void> {
    // Server revocation must succeed before reporting a completed logout.
    await this.request("/auth/logout", "POST", {});
    this.epoch += 1;
    this.access = null;
    await this.store?.clear();
  }
}
